import { bookingInquiryStrings } from '@data/bookingInquiry';
import { instructionStrings } from '@data/instruction';
import { Commodity } from '@models/next/Commodity';
import { Company } from '@models/next/Company';
import { LocalFile, OptionalLocalFile, RemoteFile } from '@models/next/FileType';
import {
  InstructionClientStatusName,
  InstructionStatus,
  InstructionStatusName,
} from '@models/next/Instruction';
import { PaginationType } from '@models/next/PaginationType';
import { AdminUser } from '@models/next/User';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const CustomsInstructionInsuranceTag = z.enum(['INDIVIDUAL', 'BLANKET', 'NA']);

export type CustomsInstructionInsuranceTag = z.infer<typeof CustomsInstructionInsuranceTag>;

export const CustomsInstructionInsurance = z.discriminatedUnion('tag', [
  z.object({
    tag: z.literal('INDIVIDUAL'),
    individual_insurance_files: z.array(RemoteFile),
  }),
  z.object({
    tag: z.literal('BLANKET'),
    company: z.string(),
    policy_no: z.string(),
    start: z.string(),
    end: z.string(),
    blanket_insurance_files: z.array(RemoteFile),
  }),
  z.object({
    tag: z.literal('NA'),
  }),
]);

export type CustomsInstructionInsurance = z.infer<typeof CustomsInstructionInsurance>;

export const CustomsInstructionInsuranceFormIndividual = z.object({
  tag: z.literal('INDIVIDUAL'),
  individual_insurance_files: z.array(LocalFile).label(instructionStrings.individualInsuranceFiles),
});

export const CustomsInstructionInsuranceFormLooseBlanket = z.object({
  tag: z.literal('BLANKET'),
  company: z.string().label(instructionStrings.insuranceCompany),
  policy_no: z.string().label(instructionStrings.insurancePolicyNo),
  start: f.dateString().label(instructionStrings.insurancePeriod),
  end: f.dateString().label(instructionStrings.insurancePeriodEnd),
  blanket_insurance_files: z.array(RemoteFile).label(instructionStrings.additionalFiles),
});

export const CustomsInstructionInsuranceFormNA = z.object({
  tag: z.literal('NA'),
});

export const CustomsInstructionInsuranceFormLoose = z.discriminatedUnion('tag', [
  CustomsInstructionInsuranceFormNA,
  CustomsInstructionInsuranceFormIndividual,
  CustomsInstructionInsuranceFormLooseBlanket,
]);

export const CustomsInstructionInsuranceForm = z.discriminatedUnion('tag', [
  CustomsInstructionInsuranceFormNA,
  CustomsInstructionInsuranceFormIndividual,
  z.object({
    ...CustomsInstructionInsuranceFormLooseBlanket.shape,
    company: CustomsInstructionInsuranceFormLooseBlanket.shape.company.min(1),
    policy_no: CustomsInstructionInsuranceFormLooseBlanket.shape.policy_no.min(1),
    start: CustomsInstructionInsuranceFormLooseBlanket.shape.start.min(1),
    end: CustomsInstructionInsuranceFormLooseBlanket.shape.end.min(1),
  }),
]);

export type CustomsInstructionInsuranceForm = z.infer<typeof CustomsInstructionInsuranceForm>;

export const CustomsInstructionSpecialNoteItem = z.object({
  title: z.string(),
  selected: z.boolean(),
  note: z.string().label(instructionStrings.specialNoteNote),
});

export type CustomsInstructionSpecialNoteItem = z.infer<typeof CustomsInstructionSpecialNoteItem>;

export const CustomsInstructionFields = z.object({
  custom_files: RemoteFile.array().label(instructionStrings.customsFiles),
  commodities: Commodity.array().label(instructionStrings.commodities),
  commodities_id: z.number().array().label(instructionStrings.commodities),
  has_special_note: z.boolean().label(instructionStrings.specialNote),
  special_note: CustomsInstructionSpecialNoteItem.array().label(
    instructionStrings.specialNoteDetails,
  ),
  insurance: CustomsInstructionInsurance.nullish().label(instructionStrings.seaInsuranceUsage),
  rcep_file: RemoteFile.nullish().label(instructionStrings.rcepFile),
  status_name: InstructionStatusName.label(instructionStrings.status),
  client_status_name: z
    .enum(['すべて', ...InstructionClientStatusName.options])
    .label(instructionStrings.status),
  writer_id: z.number().nullish(),
  writer: AdminUser.nullish(),
  writer_company: Company.nullish(),
  cutoff_date: f.dateString(),
  status: InstructionStatus.array(),
  use_rcep_changed: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type CustomsInstructionFields = z.infer<typeof CustomsInstructionFields>;

export const CustomsInstructionSpecialNoteItemForm = z
  .object({
    title: CustomsInstructionSpecialNoteItem.shape.title,
    selected: CustomsInstructionSpecialNoteItem.shape.selected,
    note: CustomsInstructionSpecialNoteItem.shape.note,
  })
  .superRefine((data, ctx) => {
    if (data.selected) {
      const e = z
        .object({
          note: CustomsInstructionSpecialNoteItem.shape.note,
        })
        .safeParse(data);
      !e.success && e.error.issues.forEach(ctx.addIssue);
    }
  });

export type CustomsInstructionSpecialNoteItemForm = z.infer<
  typeof CustomsInstructionSpecialNoteItemForm
>;

/**
 * save: 一時保存
 * submit: submit anyways (for some allowed errors)
 * attempt: try to submit, if no error, just submit
 */
export const CustomsInstructionFormAction = z.enum(['save', 'submit', 'attempt']);

export type CustomsInstructionFormAction = z.infer<typeof CustomsInstructionFormAction>;

export const CustomsInstructionFormStrict = z.object({
  custom_files: LocalFile.array().label(instructionStrings.customsFiles).min(1),
  commodities: CustomsInstructionFields.shape.commodities_id.min(1),
  has_special_note: CustomsInstructionFields.shape.has_special_note,
  special_note: CustomsInstructionSpecialNoteItemForm.array(),
  insurance: CustomsInstructionInsuranceForm,
  rcep_file: OptionalLocalFile.label(instructionStrings.rcepFile),
  action: CustomsInstructionFormAction,
});

export const CustomsInstructionForm = z
  .object({
    custom_files: LocalFile.array().label(instructionStrings.customsFiles),
    commodities: CustomsInstructionFields.shape.commodities_id,
    has_special_note: CustomsInstructionFields.shape.has_special_note,
    special_note: CustomsInstructionSpecialNoteItemForm.array().label(
      instructionStrings.specialNoteDetails,
    ),
    insurance: CustomsInstructionInsuranceFormLoose.label(instructionStrings.seaInsuranceUsage),
    rcep_file: OptionalLocalFile.label(instructionStrings.rcepFile),
    action: CustomsInstructionFormAction,
  })
  .superRefine((data, ctx) => {
    if (data.action !== 'save') {
      const e = CustomsInstructionFormStrict.safeParse(data);
      !e.success && e.error.issues.forEach(ctx.addIssue);
    }

    if (data.has_special_note && data.special_note.every((s) => s.selected === false)) {
      ctx.addIssue({
        code: 'custom',
        path: ['special_note'],
        message: instructionStrings.errorEmptySpecialNote,
      });
    }
  });

export type CustomsInstructionForm = z.infer<typeof CustomsInstructionForm>;

export const CustomsInstruction = z.object({
  custom_files: CustomsInstructionFields.shape.custom_files,
  commodities: CustomsInstructionFields.shape.commodities,
  has_special_note: CustomsInstructionFields.shape.has_special_note,
  special_note: CustomsInstructionFields.shape.special_note,
  insurance: CustomsInstructionFields.shape.insurance,
  rcep_file: CustomsInstructionFields.shape.rcep_file,

  writer_id: CustomsInstructionFields.shape.writer_id,

  cutoff_date: CustomsInstructionFields.shape.cutoff_date,
  status: CustomsInstructionFields.shape.status,
  use_rcep_changed: CustomsInstructionFields.shape.use_rcep_changed,
  created_at: CustomsInstructionFields.shape.created_at,
  updated_at: CustomsInstructionFields.shape.updated_at,
});

export type CustomsInstruction = z.infer<typeof CustomsInstruction>;

export const CustomsInstructionAdmin = z.object({
  ...CustomsInstruction.shape,
  writer: CustomsInstructionFields.shape.writer.nullish(),
  writer_company: CustomsInstructionFields.shape.writer_company.nullish(),
});

export type CustomsInstructionAdmin = z.infer<typeof CustomsInstructionAdmin>;

export const CustomsInstructionListItem = z.object({
  d2d_no: z.string(),
  cutoff_date: CustomsInstructionFields.shape.cutoff_date,
  status: CustomsInstructionFields.shape.status,
  eta: z.string().nullish(),
  etd: z.string().nullish(),
  created_at: CustomsInstructionFields.shape.created_at,
  updated_at: CustomsInstructionFields.shape.updated_at,
});

export type CustomsInstructionListItem = z.infer<typeof CustomsInstructionListItem>;

export const CustomsInstructionListItemPages = z.object({
  count: z.number(),
  results: CustomsInstructionListItem.array(),
});

export type CustomsInstructionListItemPages = z.infer<typeof CustomsInstructionListItemPages>;

export const PatchCustomsInstruction = z
  .object({
    custom_files: CustomsInstructionFields.shape.custom_files,
    commodities: CustomsInstructionFields.shape.commodities_id,
    has_special_note: CustomsInstructionFields.shape.has_special_note,
    special_note: CustomsInstructionFields.shape.special_note,
    insurance: CustomsInstructionFields.shape.insurance,
    rcep_file: CustomsInstructionFields.shape.rcep_file,

    status_name: CustomsInstructionFields.shape.status_name,
    message: z.string(),
  })
  .partial();

export type PatchCustomsInstruction = z.infer<typeof PatchCustomsInstruction>;

export const CustomsInstructionSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    d2d_no: z.string().label(bookingInquiryStrings.d2dNo),
    status: CustomsInstructionFields.shape.status_name.array(),
  })
  .partial();

export type CustomsInstructionSearchQuery = z.infer<typeof CustomsInstructionSearchQuery>;

export const CustomsInstructionSearchForm = z.object({
  d2d_no: z.string().label(bookingInquiryStrings.d2dNo),
  status: CustomsInstructionFields.shape.client_status_name,
});

export type CustomsInstructionSearchForm = z.infer<typeof CustomsInstructionSearchForm>;

export const CustomsInstructionAdminPages = z.object({
  count: z.number(),
  results: CustomsInstructionAdmin.array(),
});

export type CustomsInstructionAdminPages = z.infer<typeof CustomsInstructionAdminPages>;
